import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoFiltersCard from "../../../components/eco-ui/EcoFiltersCard";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { downloadBlob } from "../../dp/utils/format";
import { HOMEPAGE } from "../../home/routes";
import {
  ACTOR_TYPE_LABELS,
  AuditActorType,
  AuditLogDetail,
  AuditLogFilters,
  AuditLogRow,
  MODULE_LABELS,
  exportAuditLogsApi,
  getAuditLogApi,
  listAuditLogsApi,
} from "../api/auditApi";
import { ADMIN_SISTEMA_PAGE } from "../routes";
import "./AdminSistema.css";
import "../../dp/pages/DpEco.css";
import "../../dp/pages/Dp.css";

/** "AAAA-MM-DD" no fuso local. */
const isoDay = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Padrão da tela: os últimos 7 dias, hoje incluído. */
const defaultPeriod = () => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - 6);
  return { from: isoDay(start), to: isoDay(today) };
};

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" });

const shortId = (id: string | null) => (id ? (id.length > 12 ? `${id.slice(0, 8)}…` : id) : "");

const TEXT_KEYS = ["actor", "action", "q"] as const;
type TextKey = (typeof TEXT_KEYS)[number];

/**
 * Administração → Sistema → Logs de auditoria (ADR 0035). Tudo o que altera dado
 * no sistema, em qualquer mundo de token, com quem, quando, o quê, em qual
 * registro, de onde e se deu certo. Os filtros ficam na URL: o "Ver histórico" e
 * o Voltar do navegador mantêm a consulta.
 */
export default function AuditLogsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(ADMIN_SISTEMA_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [params, setParams] = useSearchParams();

  const period = useMemo(defaultPeriod, []);
  const from = params.get("de") ?? period.from;
  const to = params.get("ate") ?? period.to;
  const filters: AuditLogFilters = useMemo(
    () => ({
      from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
      actor: params.get("actor") ?? "",
      actorType: (params.get("actorType") ?? "") as AuditActorType | "",
      module: params.get("module") ?? "",
      action: params.get("action") ?? "",
      entityType: params.get("entityType") ?? "",
      entityId: params.get("entityId") ?? "",
      success: params.get("falhas") === "1" ? "false" : "",
      q: params.get("q") ?? "",
    }),
    [params, from, to],
  );

  /** Rascunho dos campos de texto: aplicados no Enter ou ao sair do campo. */
  const [drafts, setDrafts] = useState<Record<TextKey, string>>({ actor: "", action: "", q: "" });
  useEffect(() => {
    setDrafts({ actor: params.get("actor") ?? "", action: params.get("action") ?? "", q: params.get("q") ?? "" });
  }, [params]);

  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState<AuditLogDetail | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listAuditLogsApi(filters);
      setRows(data.items);
      setNextCursor(data.nextCursor);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Erro ao carregar os logs de auditoria.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const data = await listAuditLogsApi(filters, nextCursor);
      setRows((prev) => [...prev, ...data.items]);
      setNextCursor(data.nextCursor);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao carregar mais registros.");
    } finally {
      setLoadingMore(false);
    }
  };

  const openDetail = async (id: string) => {
    setOpeningId(id);
    try {
      setDetail(await getAuditLogApi(id));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao abrir o registro do log.");
    } finally {
      setOpeningId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAuditLogsApi(filters);
      downloadBlob(blob, `logs-de-auditoria-${from || "inicio"}-a-${to || "hoje"}.csv`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao exportar os logs de auditoria.");
    } finally {
      setExporting(false);
    }
  };

  const showHistory = (entityType: string | null, entityId: string | null) => {
    if (!entityType || !entityId) return;
    // O histórico de um registro olha para trás sem limite de período.
    const next = new URLSearchParams();
    next.set("entityType", entityType);
    next.set("entityId", entityId);
    next.set("de", "");
    next.set("ate", "");
    setDetail(null);
    setParams(next);
  };

  const activeCount =
    (params.has("de") || params.has("ate") ? 1 : 0) +
    ["actor", "actorType", "module", "action", "q", "falhas", "entityId"].filter((k) => params.get(k)).length;
  const summary = [
    from || to ? `${from ? from.split("-").reverse().join("/") : "início"} a ${to ? to.split("-").reverse().join("/") : "hoje"}` : "Todo o período",
    filters.success === "false" ? "só falhas" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (!token) return null;

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <div className="admAuditPage">
          <EcoPageHeader
            crumbs={[
              { label: "Início", onClick: () => navigate(HOMEPAGE()) },
              { label: "Administração do Sistema", onClick: () => navigate(ADMIN_SISTEMA_PAGE()) },
              { label: "Logs de auditoria" },
            ]}
            title="Logs de auditoria"
            subtitle="Tudo o que alterou dado no sistema: quem, quando, o quê, em qual registro, de onde e se deu certo."
            onBack={goBack}
            backLabel="Voltar para a Administração"
            actions={
              <button type="button" className="ecoBtn" onClick={handleExport} disabled={exporting}>
                <EcoIcon name="download" size={18} />
                {exporting ? "Exportando..." : "Exportar CSV"}
              </button>
            }
          />

          <EcoFiltersCard summary={summary} activeCount={activeCount}>
            <div className="ecoFilters__grid">
              <div className="ecoField">
                <label htmlFor="al-de">De</label>
                <input id="al-de" className="ecoInput" type="date" value={from} max={to || undefined} onChange={(e) => setParam("de", e.target.value || "")} />
              </div>
              <div className="ecoField">
                <label htmlFor="al-ate">Até</label>
                <input id="al-ate" className="ecoInput" type="date" value={to} min={from || undefined} onChange={(e) => setParam("ate", e.target.value || "")} />
              </div>
              {TEXT_KEYS.map((key) => (
                <div className="ecoField" key={key}>
                  <label htmlFor={`al-${key}`}>{key === "actor" ? "Pessoa" : key === "action" ? "Ação" : "Texto no resumo"}</label>
                  <input
                    id={`al-${key}`}
                    className="ecoInput"
                    type="search"
                    placeholder={key === "actor" ? "Nome de quem fez" : key === "action" ? "Ex.: quiz. ou auth.login" : "Ex.: encerrou"}
                    value={drafts[key]}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                    onBlur={() => drafts[key].trim() !== (params.get(key) ?? "") && setParam(key, drafts[key].trim())}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setParam(key, drafts[key].trim());
                    }}
                  />
                </div>
              ))}
              <div className="ecoField">
                <label htmlFor="al-tipo">Tipo de ator</label>
                <select id="al-tipo" className="ecoSelect ecoSelect--sm" value={filters.actorType} onChange={(e) => setParam("actorType", e.target.value)}>
                  <option value="">Todos</option>
                  {(Object.keys(ACTOR_TYPE_LABELS) as AuditActorType[]).map((t) => (
                    <option key={t} value={t}>
                      {ACTOR_TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ecoField">
                <label htmlFor="al-mod">Módulo</label>
                <select id="al-mod" className="ecoSelect ecoSelect--sm" value={filters.module} onChange={(e) => setParam("module", e.target.value)}>
                  <option value="">Todos</option>
                  {Object.entries(MODULE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="ecoFilters__row">
              <label className="admAuditCheck">
                <input type="checkbox" checked={filters.success === "false"} onChange={(e) => setParam("falhas", e.target.checked ? "1" : "")} />
                Só falhas (negado ou erro)
              </label>
              <button type="button" className="ecoBtn ecoFilters__clear" onClick={() => setParams({}, { replace: true })}>
                Limpar filtros
              </button>
            </div>
          </EcoFiltersCard>

          {filters.entityType && filters.entityId ? (
            <div className="admAuditHistory">
              <EcoIcon name="clock" size={16} />
              <span>
                Histórico do registro <strong>{filters.entityType}</strong> <code>{filters.entityId}</code>
              </span>
              <button
                type="button"
                className="ecoLinkBtn"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.delete("entityType");
                  next.delete("entityId");
                  setParams(next, { replace: true });
                }}
              >
                Sair do histórico
              </button>
            </div>
          ) : null}

          <section className="ecoCard">
            {loadError ? (
              <div className="admSettingsError">
                <p>{loadError}</p>
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={load}>
                  Tentar novamente
                </button>
              </div>
            ) : (
              <div className="ecoTableWrap">
                <table className="ecoTable admAuditTable">
                  <thead>
                    <tr>
                      <th>Data e hora</th>
                      <th>Quem</th>
                      <th>Ação</th>
                      <th>Registro</th>
                      <th>Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="ecoEmpty">
                          Carregando...
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="ecoEmpty">
                          Nenhum registro com esses filtros.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr
                          key={r.id}
                          className={`admAuditRow${r.success ? "" : " admAuditRow--fail"}`}
                          tabIndex={0}
                          onClick={() => openDetail(r.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") openDetail(r.id);
                          }}
                          aria-busy={openingId === r.id}
                        >
                          <td data-label="Quando" className="ecoTable__muted admAuditTable__when">
                            {fmtDateTime(r.occurredAt)}
                          </td>
                          <td data-label="Quem">
                            <span className="admAuditWho">
                              <span>{r.actorLabel}</span>
                              <span className="admAuditSub">{ACTOR_TYPE_LABELS[r.actorType]}</span>
                            </span>
                          </td>
                          <td data-label="">
                            <span className="admAuditWhat">
                              <span>{r.summary}</span>
                              <span className="admAuditSub">
                                {r.action}
                                {r.module ? ` · ${MODULE_LABELS[r.module] ?? r.module}` : ""}
                              </span>
                            </span>
                          </td>
                          <td data-label="Registro" className="ecoTable__muted">
                            {r.entityType ? (
                              <span title={r.entityId ?? undefined}>
                                {r.entityType}
                                {r.entityId ? ` ${shortId(r.entityId)}` : ""}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td data-label="Resultado">
                            {r.success ? (
                              <span className="ecoPill">Sucesso</span>
                            ) : (
                              <span className="ecoPill admAuditPill--fail">Falha{r.statusCode ? ` ${r.statusCode}` : ""}</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {nextCursor && !loading ? (
              <div className="admAuditMore">
                <button type="button" className="ecoBtn" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Carregando..." : "Carregar mais"}
                </button>
              </div>
            ) : null}
          </section>
        </div>
      </main>

      {detail ? <AuditLogDetailModal log={detail} onClose={() => setDetail(null)} onHistory={showHistory} /> : null}
    </div>
  );
}

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

type DetailProps = {
  log: AuditLogDetail;
  onClose: () => void;
  onHistory: (entityType: string | null, entityId: string | null) => void;
};

/** Detalhe de uma linha: todos os campos, o antes e o depois e o corpo já sanitizado no backend. */
function AuditLogDetailModal({ log, onClose, onHistory }: DetailProps) {
  const changes = log.changes ? Object.entries(log.changes) : [];
  const fields: [string, React.ReactNode][] = [
    ["Quando", fmtDateTime(log.occurredAt)],
    ["Quem", `${log.actorLabel} · ${ACTOR_TYPE_LABELS[log.actorType]}`],
    ["Ação", log.action],
    ["Módulo", log.module ? MODULE_LABELS[log.module] ?? log.module : "—"],
    ["Registro", log.entityType ? `${log.entityType}${log.entityId ? ` · ${log.entityId}` : ""}` : "—"],
    ["Rota", log.method ? `${log.method} ${log.route ?? ""}` : "—"],
    ["Resultado", `${log.success ? "Sucesso" : "Falha"}${log.statusCode ? ` (${log.statusCode})` : ""}`],
    ["IP", log.ip ?? "—"],
    ["Navegador", log.userAgent || "—"],
  ];

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpModal--wide" role="dialog" aria-modal="true" aria-labelledby="audit-detail-title">
        <header className="dpModal__header">
          <h2 className="dpModal__title" id="audit-detail-title">
            {log.summary}
          </h2>
          <button type="button" className="dpModal__close" onClick={onClose} aria-label="Fechar">
            <EcoIcon name="x" size={18} />
          </button>
        </header>
        <div className="dpModal__body">
          <dl className="admAuditFields">
            {fields.map(([label, value]) => (
              <div key={label} className="admAuditFields__item">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>

          {changes.length ? (
            <>
              <h3 className="admAuditDetail__title">Antes → Depois</h3>
              <div className="ecoTableWrap">
                <table className="ecoTable">
                  <thead>
                    <tr>
                      <th>Campo</th>
                      <th>Antes</th>
                      <th>Depois</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map(([field, change]) => (
                      <tr key={field}>
                        <td>{field}</td>
                        <td className="ecoTable__muted">{formatValue(change?.before)}</td>
                        <td>{formatValue(change?.after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          <h3 className="admAuditDetail__title">Dados enviados</h3>
          {log.payload ? (
            <pre className="admAuditPayload">{JSON.stringify(log.payload, null, 2)}</pre>
          ) : (
            <p className="admAuditSub">Nada gravado (rota sem corpo, ou corpo com dado sensível que não vai para o log).</p>
          )}
        </div>
        <footer className="dpModal__footer">
          {log.entityType && log.entityId ? (
            <button type="button" className="ecoBtn" onClick={() => onHistory(log.entityType, log.entityId)}>
              <EcoIcon name="clock" size={16} />
              Ver histórico deste registro
            </button>
          ) : null}
          <button type="button" className="ecoBtn ecoBtn--primary" onClick={onClose}>
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}
